/**
 * RequestServiceWidget — embeddable "What do you need help with?"
 * card that sister sites (KeralaHealth, TravelerPulse, NaturePulse…)
 * drop onto any article, product, or landing page to route contextual
 * demand into the SocialPulse Services marketplace.
 *
 * Usage:
 *   <RequestServiceWidget
 *     title="Need help planning your medical visit?"
 *     placeholder="e.g. Airport pickup for parents arriving from Riyadh..."
 *     services={["patient-coordinator","medical-transport","medical-interpreter","home-nurse"]}
 *   />
 *
 * On submit → navigates to /services/request?prefill=… (which will
 * auto-run the AI intake) — no double-typing for the user.
 */
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { trackIntel } from "@/lib/analytics";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, ArrowRight } from "lucide-react";

const SERVICE_LABELS = {
  "airport-transfer":     "Airport transfers",
  "driver-hire":          "Drivers",
  "tour-guide":           "Tour guides",
  "travel-planner":       "Travel planners",
  "travel-photographer":  "Photographers",
  "patient-coordinator":  "Medical coordinators",
  "medical-transport":    "Medical transport",
  "medical-interpreter":  "Interpreters",
  "home-nurse":           "Home nurses",
  "physiotherapist":      "Physiotherapy",
  "event-photographer":   "Event photographers",
  "event-videographer":   "Videographers",
  "caterer":              "Caterers",
  "florist":              "Florists",
  "event-planner":        "Event planners",
  "memorial-services":    "Memorial services",
};

export default function RequestServiceWidget({
  title = "Need a hand with anything?",
  subtitle = "Describe what you need and we'll match you with qualified providers.",
  placeholder = "e.g. I need someone to pick up my parents from Kochi airport around 9 PM Nov 12...",
  services = [],
  ctaLabel = "Find providers",
  compact = false,
}) {
  const nav = useNavigate();
  const [text, setText] = useState("");

  useEffect(() => {
    trackIntel("widget_impression", { widget: "request_service", services });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = (prefill) => {
    trackIntel("widget_click", { widget: "request_service", has_prefill: !!prefill });
    const qs = new URLSearchParams();
    if (prefill) qs.set("prefill", prefill);
    nav(`/hire/request?${qs.toString()}`);
  };

  const submit = () => go(text.trim());

  return (
    <Card className="p-5 sm:p-6 border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white" data-testid="request-service-widget">
      <div className="flex items-start gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
        <div>
          <h3 className="font-semibold text-slate-900">{title}</h3>
          {subtitle && <p className="text-sm text-slate-600 mt-0.5">{subtitle}</p>}
        </div>
      </div>

      {!compact && (
        <div className="mb-3">
          <Textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={placeholder}
            rows={3}
            data-testid="widget-text-input"
            className="text-sm bg-white"
          />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          onClick={submit}
          disabled={!compact && !text.trim()}
          data-testid="widget-submit-btn"
          className="flex-1"
        >
          {ctaLabel}
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>

      {services.length > 0 && (
        <div className="mt-4 pt-4 border-t border-emerald-100">
          <div className="text-xs text-slate-500 mb-2 font-medium">Popular services here:</div>
          <div className="flex flex-wrap gap-1.5">
            {services.map(slug => (
              <button
                key={slug}
                onClick={() => {
                  const qs = new URLSearchParams();
                  qs.set("category", slug);
                  nav(`/hire/request?${qs.toString()}`);
                }}
                data-testid={`widget-service-${slug}`}
                className="px-2.5 py-1 rounded-full text-xs bg-white border border-slate-200 hover:border-emerald-400 hover:text-emerald-700 transition"
              >
                {SERVICE_LABELS[slug] || slug}
              </button>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
